import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SERVICE_URL } from '../../constants/url.constants';
import {
    IBlock,
    IClientIdMapping,
    IDistrict,
    IGeoLocationMapping,
    IPanchayat,
    IState,
    IVillage,
} from '../../models/cdn.model';

@Injectable({
    providedIn: 'root',
})
export class CdnService {
    private readonly http = inject(HttpClient);

    private readonly cdnMasterDataUrl = environment.cdnUrl + SERVICE_URL.CDN_MASTER;
    private readonly cdnKeycloakUrl = environment.cdnUrl + SERVICE_URL.CDN_KEYCLOAK;

    getStatesList(): Observable<IState[]> {
        const url = `${this.cdnMasterDataUrl}statemaster.json`;
        return this.http.get<IState[]>(url).pipe(
            map((states) => {
                const filtered = states.filter((state) =>
                    environment.production ? String(state.state_id) !== '91' && String(state.state_id) !== '92' : true,
                );
                return this.sortByProperty(filtered, 'state_name_en');
            }),
        );
    }

    getDistrictsList(stateId: string | number): Observable<IDistrict[]> {
        const formattedStateId = this.formatStateId(stateId);
        const url = `${this.cdnMasterDataUrl}state/${formattedStateId}.json`;
        return this.http
            .get<IDistrict[]>(url)
            .pipe(map((districts) => this.sortByProperty(districts, 'district_name_en')));
    }

    getBlockList(stateId: string | number, districtId: string | number): Observable<IBlock[]> {
        const formattedStateId = this.formatStateId(stateId);
        const url = `${this.cdnMasterDataUrl}state/${formattedStateId}/district/${districtId}.json`;
        return this.http.get<IBlock[]>(url).pipe(map((blocks) => this.sortByProperty(blocks, 'block_name_en')));
    }

    getPanchayatList(
        stateId: string | number,
        districtId: string | number,
        blockId: string | number,
    ): Observable<IPanchayat[]> {
        const formattedStateId = this.formatStateId(stateId);
        const url = `${this.cdnMasterDataUrl}state/${formattedStateId}/district/${districtId}/block/${blockId}.json`;
        return this.http.get<IPanchayat[]>(url);
    }

    getVillageList(
        stateId: string | number,
        districtId: string | number,
        blockId: string | number,
        panchayatId: string | number,
    ): Observable<IVillage[]> {
        const formattedStateId = this.formatStateId(stateId);
        const url = `${this.cdnMasterDataUrl}state/${formattedStateId}/district/${districtId}/block/${blockId}/panchayat/${panchayatId}.json`;
        return this.http.get<IVillage[]>(url);
    }

    getGeoLocationMapping(): Observable<IGeoLocationMapping[]> {
        const url = `${this.cdnKeycloakUrl}geo-map.json`;
        return this.http.get<IGeoLocationMapping[]>(url);
    }

    getClientIdsMapping(): Observable<IClientIdMapping[]> {
        const url = `${this.cdnKeycloakUrl}client-map.json`;
        return this.http.get<IClientIdMapping[]>(url);
    }

    // Utils
    private formatStateId(stateId: string | number): string {
        return `${stateId}`.padStart(2, '0');
    }

    private sortByProperty<T>(list: T[], key: keyof T): T[] {
        return [...list].sort((a, b) => {
            const valA = String(a[key] ?? '');
            const valB = String(b[key] ?? '');
            return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
        });
    }
}
